import {CorsWorker} from "#browser-utils/cors-worker"

export interface IWorkerLike {
    addEventListener<K extends keyof WorkerEventMap>(
        type: K,
        listener: (this: Worker, ev: WorkerEventMap[K]) => unknown,
        options?: boolean | AddEventListenerOptions,
    ): void
    addEventListener(type: string, listener: EventListenerOrEventListenerObject, options?: boolean | AddEventListenerOptions): void
    removeEventListener<K extends keyof WorkerEventMap>(
        type: K,
        listener: (this: Worker, ev: WorkerEventMap[K]) => unknown,
        options?: boolean | EventListenerOptions,
    ): void
    removeEventListener(type: string, listener: EventListenerOrEventListenerObject, options?: boolean | EventListenerOptions): void
    postMessage(message: unknown, transfer?: Transferable[]): void
    terminate(): void
}

/**
 * Drop-in wrapper around a Worker that transparently handles same-origin vs cross-origin:
 * - Same-origin: creates a plain Worker.
 * - Cross-origin: wraps the URL in a blob URL (via CorsWorker) to bypass the strict same-origin
 *   policy, buffers outbound postMessage calls until the async module import signals readiness
 *   ({__workerReady: true}), and filters that sentinel from inbound message events.
 */
export class CorsAwareWorker implements IWorkerLike {
    private readonly _worker: Worker
    private readonly _corsWorker?: CorsWorker
    private _ready: boolean
    private _pendingMessages: {message: unknown; transfer: Transferable[]}[] = []
    // Maps each external listener to its internally wrapped version so removeEventListener works.
    private readonly _wrappedListeners = new WeakMap<EventListenerOrEventListenerObject, EventListenerOrEventListenerObject>()

    /**
     * @param nativeWorkerFactory Optional factory that creates the Worker for the same-origin case.
     * Pass `() => new Worker(new URL(path, import.meta.url), opts)` so that bundlers (Vite, esbuild)
     * can detect the literal `new Worker(new URL(...))` pattern at the call site and create a
     * compiled worker bundle. Without the literal in caller code the bundler won't bundle the file.
     * CorsAwareWorker still centrally handles origin detection and CORS logic.
     */
    constructor(url: string | URL, options?: WorkerOptions, nativeWorkerFactory?: () => Worker) {
        const isSameOrigin = new URL(url.toString(), location.href).origin === location.origin
        if (isSameOrigin) {
            this._worker = nativeWorkerFactory ? nativeWorkerFactory() : new Worker(url, options)
            this._ready = true
        } else {
            this._corsWorker = new CorsWorker(url, options)
            this._worker = this._corsWorker.getWorker()
            this._ready = false
            const onReady = (event: MessageEvent) => {
                if (event.data?.__workerReady) {
                    this._worker.removeEventListener("message", onReady)
                    this._ready = true
                    for (const msg of this._pendingMessages) {
                        this._worker.postMessage(msg.message, msg.transfer)
                    }
                    this._pendingMessages = []
                }
            }
            this._worker.addEventListener("message", onReady)
        }
    }

    addEventListener<K extends keyof WorkerEventMap>(
        type: K,
        listener: (this: Worker, ev: WorkerEventMap[K]) => unknown,
        options?: boolean | AddEventListenerOptions,
    ): void
    addEventListener(type: string, listener: EventListenerOrEventListenerObject, options?: boolean | AddEventListenerOptions): void
    addEventListener(type: string, listener: EventListenerOrEventListenerObject, options?: boolean | AddEventListenerOptions): void {
        if (type === "message") {
            const wrapped: EventListener = (event: Event) => {
                if (!(event as MessageEvent).data?.__workerReady) {
                    if (typeof listener === "function") {
                        listener(event)
                    } else {
                        listener.handleEvent(event)
                    }
                }
            }
            this._wrappedListeners.set(listener, wrapped)
            this._worker.addEventListener(type, wrapped, options)
        } else {
            this._worker.addEventListener(type, listener, options)
        }
    }

    removeEventListener<K extends keyof WorkerEventMap>(
        type: K,
        listener: (this: Worker, ev: WorkerEventMap[K]) => unknown,
        options?: boolean | EventListenerOptions,
    ): void
    removeEventListener(type: string, listener: EventListenerOrEventListenerObject, options?: boolean | EventListenerOptions): void
    removeEventListener(type: string, listener: EventListenerOrEventListenerObject, options?: boolean | EventListenerOptions): void {
        if (type === "message") {
            const wrapped = this._wrappedListeners.get(listener)
            if (wrapped) {
                this._wrappedListeners.delete(listener)
                this._worker.removeEventListener(type, wrapped, options)
            }
        } else {
            this._worker.removeEventListener(type, listener, options)
        }
    }

    postMessage(message: unknown, transfer?: Transferable[]): void {
        if (this._ready) {
            this._worker.postMessage(message, transfer ?? [])
        } else {
            this._pendingMessages.push({message, transfer: transfer ?? []})
        }
    }

    terminate(): void {
        this._corsWorker?.revokeBlobUrl()
        this._worker.terminate()
    }
}
